import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './AccountDropdown.scss';
import env from '@/env';
import { getSubdomain, getWebAppInfo } from '@/utils';
import { Paths } from '@/pages/routers';
import { navigate, useLocation } from '@reach/router';
import { RootState } from '@/redux/reducers';
import { resetActions } from '@/redux/actions/reset';
import { logoutUserActions } from '@/redux/actions/auth';
import { fetchEntityDashboardActions } from '@/redux/actions/service-directory/entity';
import Dropdown from '@/components/Dropdown/Dropdown';
import IcoFont from '@/components/IcoFont';
import iconUserGray from '@/assets/icons/user.svg';

interface AccountProps {
  textColor?: string;
  iconUser?: string;
}

enum AuthDropdownValue {
  logout = 'logout',
  dashboard = 'dashboard',
}

const Account: React.FC<AccountProps> = ({ textColor, iconUser }) => {
  const webApp = getWebAppInfo(env.siteId);
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const authUser = useSelector((state: RootState) => state.authUser.data);
  const { userInfo } = useSelector((state: RootState) => state.membershipUsers);
  const organisation = useSelector((state: RootState) => state.serviceDirectoryEntity.entityDashboard);
  const [outsideDashboard, setOutsideDashboard] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const subdomain = getSubdomain();
  const hasSubdomain = subdomain && subdomain !== 'www';

  const userDropdownOptions = [
    {
      label: (
        <>
          <IcoFont icon="dashboard" />
          <span style={{ marginLeft: 8 }}>Dashboard</span>
        </>
      ),
      value: 'dashboard',
      showCondition: outsideDashboard,
    },
    {
      label: (
        <>
          <IcoFont icon="logout" />
          <span style={{ marginLeft: 8 }}>Logout</span>
        </>
      ),
      value: 'logout',
    },
  ];

  const handleChangeDropdownUser = (value: string) => {
    switch (value) {
      case AuthDropdownValue.logout:
        dispatch(logoutUserActions.request());
        break;
      case AuthDropdownValue.dashboard:
        !isAdmin
          ? navigate(
              `https://${organisation?.subdomain || 'www'}.${webApp.domain}${Paths.Dashboard}${Paths.MyOrganisation}`
            )
          : navigate(`https://www.${webApp.domain}${Paths.Dashboard}${Paths.MyDirectory}`);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    setOutsideDashboard(!pathname.includes('/dashboard'));
  }, [pathname]);

  useEffect(() => {
    authUser &&
      outsideDashboard &&
      !isAdmin &&
      dispatch(fetchEntityDashboardActions.request({ entityId: authUser.organisationId }));
  }, [dispatch, authUser, outsideDashboard, hasSubdomain, isAdmin]);

  useEffect(() => {
    return () => {
      dispatch(resetActions.reset('FETCH_ENTITY_DASHBOARD'));
    };
  }, [dispatch]);

  useEffect(() => {
    if (authUser) {
      const roles = !userInfo.memberships.length
        ? authUser?.roles
        : [
            ...authUser?.roles,
            userInfo.memberships.find((entity) => entity.organisationId === organisation?.id)?.roleName || '',
          ];
      setIsAdmin(roles.includes(env.roles.directoryAdmin) || roles.includes(env.roles.superAdmin));
    }
  }, [authUser, userInfo, organisation]);

  return (
    <div className="Account">
      <Dropdown
        options={userDropdownOptions}
        placeholder={
          <>
            <img src={iconUser || iconUserGray} alt="User" />
            <span style={{ color: textColor }}>Welcome {authUser?.firstName}</span>
          </>
        }
        onChange={(e: { value: string }) => handleChangeDropdownUser(e.value)}
        className="PublisherHeader-user"
      />
    </div>
  );
};

export default Account;
